<template>
  <div ref="fullscreenDiv" class="dash-page" :class="{ fullscreen: isFullscreen }">
    <block-loader v-if="loading" />
    <div class="dash">
      <!-- Cabeçalho -->
      <div class="dash-head">
        <div>
          <h1>
            {{ $t('DASHBOARD.HOME.HEADING') }}
            <span class="live">
              <span class="pulse" /> {{ $t('DASHBOARD.HOME.LIVE') }}
            </span>
          </h1>
          <div class="sub">{{ $t('DASHBOARD.HOME.SUBTITLE') }}</div>
        </div>
        <div class="dash-filters">
          <label class="fsel">
            <svg class="fsel-ic" viewBox="0 0 24 24">
              <path
                d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.9"
              />
            </svg>
            <select :value="type" @change="onDashTypeChange">
              <option value="inbox">{{ $t('DASHBOARD.INBOX.INBOX') }}</option>
              <option value="team">{{ $t('DASHBOARD.TEAM') }}</option>
            </select>
          </label>
          <label class="fsel">
            <svg class="fsel-ic" viewBox="0 0 24 24">
              <path d="M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1.5" />
            </svg>
            <select v-model="dashFilterId">
              <option v-for="opt in dashFilterOptions" :key="opt.id" :value="opt.id">
                {{ opt.name }}
              </option>
            </select>
          </label>
          <label class="fsel">
            <svg class="fsel-ic" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18M8 2v4M16 2v4" />
            </svg>
            <select v-model.number="dashDurationDays">
              <option v-for="d in durationOptions" :key="d.value" :value="d.value">
                {{ d.label }}
              </option>
            </select>
          </label>
          <button
            class="fbtn"
            :title="$t('DASHBOARD.HOME.REFRESH')"
            @click="refreshData"
          >
            <svg viewBox="0 0 24 24">
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
            </svg>
          </button>
          <button
            class="fbtn"
            :title="$t('DASHBOARD.HOME.FULLSCREEN')"
            @click="toggleFullscreen"
          >
            <svg viewBox="0 0 24 24">
              <path
                d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- ===== AGORA (tempo real) ===== -->
      <div class="sec-label">
        {{ $t('DASHBOARD.HOME.NOW') }}
        <span class="hint">· {{ $t('DASHBOARD.HOME.NOW_HINT') }}</span>
      </div>
      <div class="grid g3">
        <!-- Conversas -->
        <div class="card">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.CONVERSATION.TITLE') }}</h3>
            <button class="more" @click="goToConversations">
              {{ $t('DASHBOARD.HOME.OPEN_QUEUE') }}
            </button>
          </div>
          <div class="stat-row">
            <div class="stat">
              <div class="n">{{ accountConversationMetric.open || 0 }}</div>
              <div class="l">
                <span class="d dot-brand" />{{ $t('DASHBOARD.CONVERSATION.OPEN') }}
              </div>
            </div>
            <div class="vsep" />
            <div class="stat attn">
              <div class="n">{{ accountConversationMetric.unattended || 0 }}</div>
              <div class="l">
                <span class="d dot-warn" />{{ $t('DASHBOARD.CONVERSATION.NO_SERVED') }}
              </div>
            </div>
            <div class="vsep" />
            <div class="stat crit">
              <div class="n">{{ accountConversationMetric.unassigned || 0 }}</div>
              <div class="l">
                <span class="d dot-crit" />{{ $t('DASHBOARD.CONVERSATION.NOT_ASSIGNED') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Assistentes (bot) -->
        <div class="card">
          <div class="card-h"><h3>{{ $t('DASHBOARD.BOT_CONVERSATIONS') }}</h3></div>
          <div class="stat-row">
            <div class="stat">
              <div class="n">{{ accountSummary.bot_dashboard_conversation_count || 0 }}</div>
              <div class="l"><span class="d dot-purple" />{{ $t('DASHBOARD.ACTIVE') }}</div>
            </div>
            <div class="vsep" />
            <div class="stat">
              <div class="n">{{ accountSummary.bot_dashboard_resolution_count || 0 }}</div>
              <div class="l"><span class="d dot-good" />{{ $t('DASHBOARD.SOLVED') }}</div>
            </div>
            <div class="vsep" />
            <div class="stat">
              <div class="n">{{ botContentionRate }}</div>
              <div class="l">{{ $t('DASHBOARD.HOME.CONTENTION') }}</div>
            </div>
          </div>
        </div>

        <!-- Agentes -->
        <div class="card">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.AGENTS_STATUS') }}</h3>
            <button class="more" @click="goToAgents">
              {{ $t('DASHBOARD.HOME.MANAGE') }}
            </button>
          </div>
          <div class="stat-row">
            <div class="stat">
              <div class="n stat-good">{{ agentStatus.online }}</div>
              <div class="l"><span class="d dot-good" />{{ $t('DASHBOARD.ONLINE') }}</div>
            </div>
            <div class="vsep" />
            <div class="stat">
              <div class="n stat-warn">{{ agentStatus.busy }}</div>
              <div class="l"><span class="d dot-warn" />{{ $t('DASHBOARD.BUSY') }}</div>
            </div>
            <div class="vsep" />
            <div class="stat">
              <div class="n stat-muted">{{ agentStatus.offline }}</div>
              <div class="l"><span class="d dot-off" />{{ $t('DASHBOARD.OFFLINE') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== DESEMPENHO ===== -->
      <div class="sec-label">
        {{ $t('DASHBOARD.HOME.PERFORMANCE') }}
        <span class="hint">· {{ $t('DASHBOARD.HOME.PERFORMANCE_HINT') }}</span>
      </div>
      <div class="grid g6">
        <div class="kpi">
          <div class="kpi-top"><span class="kpi-label">{{ $t('DASHBOARD.TODAY.CONVERSATION') }}</span></div>
          <div class="kpi-val">{{ accountSummary.conversations_count || 0 }}</div>
        </div>
        <div class="kpi">
          <div class="kpi-top"><span class="kpi-label">{{ $t('DASHBOARD.TODAY.COUNTER_RESOLUTION') }}</span></div>
          <div class="kpi-val">{{ accountSummary.resolutions_count || 0 }}</div>
          <div class="kpi-foot">
            <span class="trend-note">
              {{ $t('DASHBOARD.HOME.OF_TOTAL', { percent: resolvedPercent }) }}
            </span>
          </div>
        </div>
        <div class="kpi">
          <div class="kpi-top"><span class="kpi-label">{{ $t('DASHBOARD.TODAY.CONVERSATIONS_BY_ATTENDANT') }}</span></div>
          <div class="kpi-val">{{ conversationByAgentCounter }}</div>
          <div class="kpi-foot">
            <span class="trend-note">
              {{ $t('DASHBOARD.HOME.AGENTS_COUNT', { count: agents.length }) }}
            </span>
          </div>
        </div>
        <div class="kpi">
          <div class="kpi-top">
            <span class="kpi-label">{{ $t('DASHBOARD.HOME.WAIT_TIME') }}</span>
            <span class="kpi-ic ic-danger">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            </span>
          </div>
          <div class="kpi-val val-danger">{{ mediumWaitingTime }}</div>
        </div>
        <div class="kpi">
          <div class="kpi-top">
            <span class="kpi-label">{{ $t('DASHBOARD.HOME.RESPONSE_TIME') }}</span>
            <span class="kpi-ic ic-success">
              <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" /></svg>
            </span>
          </div>
          <div class="kpi-val val-success">{{ mediumTimeResponse }}</div>
        </div>
        <div class="kpi">
          <div class="kpi-top">
            <span class="kpi-label">{{ $t('DASHBOARD.HOME.HANDLING_TIME') }}</span>
            <span class="kpi-ic ic-warning">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 1" /></svg>
            </span>
          </div>
          <div class="kpi-val val-warning">{{ mediumTimeService }}</div>
        </div>
      </div>

      <!-- ===== GRÁFICO (POR CANAL) + CARGA POR AGENTE ===== -->
      <div class="grid g2-1 grid-mt">
        <div class="card card-inbox">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.HOME.CONVERSATIONS_BY_DAY') }}</h3>
            <span class="chart-legend">{{ currentFilterName }}</span>
          </div>
          <metric-card-inbox-team
            key="inbox-reports"
            controlled
            :header="$t('DASHBOARD.INBOX.TITLE')"
            :type="type"
            :getter-key="getterKey"
            :action-key="actionKey"
            :select-handler="handleInboxTeam"
            :filter-inbox-id="dashFilterId"
            :external-from="dashFrom"
            :external-to="dashTo"
          />
        </div>

        <div class="card">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.HOME.AGENT_LOAD') }}</h3>
            <span class="pill pill-good">
              {{
                $t('DASHBOARD.HOME.ONLINE_OF', {
                  online: agentStatus.online,
                  total: agents.length,
                })
              }}
            </span>
          </div>
          <div v-if="agentLoad.length" class="agent-list">
            <div v-for="a in agentLoad" :key="a.id" class="agent-row">
              <span class="ava" :style="{ background: a.gradient }">
                {{ a.initials }}<span class="st" :style="{ background: a.statusColor }" />
              </span>
              <div class="nm" :class="{ 'nm-off': a.status === 'offline' }">
                {{ a.name }}
                <div class="load">{{ a.loadLabel }}</div>
              </div>
              <span class="agent-bar"><i :style="{ width: a.pct + '%' }" /></span>
            </div>
          </div>
          <div v-else class="empty-hint">
            {{ $t('DASHBOARD.HOME.NO_AGENTS') }}
          </div>
        </div>
      </div>

      <!-- ===== QUALIDADE / CSAT ===== -->
      <div class="sec-label">
        {{ $t('DASHBOARD.HOME.QUALITY') }}
        <span class="hint">· {{ $t('DASHBOARD.HOME.QUALITY_HINT') }}</span>
        <button
          v-if="csatLoaded"
          class="sec-refresh"
          :title="$t('DASHBOARD.HOME.REFRESH_CSAT')"
          @click="loadCsatData"
        >
          <svg viewBox="0 0 24 24">
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      <!-- Carregado sob demanda (impacta performance) -->
      <div v-if="!csatLoaded" class="card csat-prompt">
        <div class="csat-prompt__ic">
          <svg viewBox="0 0 24 24">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" /><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
          </svg>
        </div>
        <div class="csat-prompt__txt">
          {{ $t('DASHBOARD.HOME.CSAT_ON_DEMAND') }}
        </div>
        <woot-button size="small" @click="loadCsatData">
          {{ $t('DASHBOARD.CSAT.LOAD_DATA') }}
        </woot-button>
      </div>

      <div v-else class="grid g3">
        <!-- Satisfação -->
        <div class="card">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.HOME.SATISFACTION') }}</h3>
            <span class="pill" :class="csatPill.cls">{{ csatPill.label }}</span>
          </div>
          <div class="donut-wrap">
            <svg class="donut" width="92" height="92" viewBox="0 0 42 42">
              <circle
                class="donut-track"
                cx="21"
                cy="21"
                r="15.9"
                fill="none"
                stroke-width="6"
              />
              <circle
                class="donut-val"
                cx="21"
                cy="21"
                r="15.9"
                fill="none"
                stroke-width="6"
                stroke-linecap="round"
                pathLength="100"
                :stroke-dasharray="`${csatScore} ${100 - csatScore}`"
                stroke-dashoffset="25"
              />
            </svg>
            <div>
              <div class="csat-num">{{ csatScore }}%</div>
              <div class="csat-sub">
                {{
                  $t('DASHBOARD.HOME.RESPONSES_COUNT', {
                    count: csatMetrics.totalResponseCount || 0,
                  })
                }}
              </div>
            </div>
          </div>
          <div class="satbar">
            <span class="bar-good" :style="{ width: csatPositive + '%' }" />
            <span class="bar-warn" :style="{ width: csatNeutral + '%' }" />
            <span class="bar-crit" :style="{ width: csatNegative + '%' }" />
          </div>
          <div class="sat-emojis">
            <span>😀 {{ csatPositive }}%</span>
            <span>😐 {{ csatNeutral }}%</span>
            <span>☹️ {{ csatNegative }}%</span>
          </div>
        </div>

        <!-- Indicadores -->
        <div class="card">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.HOME.CSAT_INDICATORS') }}</h3>
          </div>
          <div class="ind-list">
            <div class="ind-row">
              <span>{{ $t('DASHBOARD.HOME.RESPONSE_RATE') }}</span
              ><b>{{ csatRespRate }}%</b>
            </div>
            <div class="ind-row">
              <span>{{ $t('DASHBOARD.HOME.FCR') }}</span
              ><b>{{ csatFcr }}%</b>
            </div>
            <div class="ind-row">
              <span>{{ $t('DASHBOARD.HOME.SENT_MESSAGES') }}</span
              ><b>{{ csatMetrics.totalSentMessagesCount || 0 }}</b>
            </div>
            <div class="ind-row">
              <span>{{ $t('DASHBOARD.HOME.TOTAL_RESPONSES') }}</span
              ><b>{{ csatMetrics.totalResponseCount || 0 }}</b>
            </div>
          </div>
        </div>

        <!-- Feedback recente -->
        <div class="card">
          <div class="card-h">
            <h3>{{ $t('DASHBOARD.HOME.RECENT_FEEDBACK') }}</h3>
          </div>
          <div v-if="recentFeedback.length" class="fb-list">
            <div v-for="f in recentFeedback" :key="f.id" class="fb">
              <span class="em" :class="f.emojiClass">{{ f.emoji }}</span>
              <div class="txt">
                {{ f.message }}
                <div class="who">{{ f.contact }}<template v-if="f.agent"> · {{ f.agent }}</template></div>
              </div>
            </div>
          </div>
          <div v-else class="empty-hint">
            {{ $t('DASHBOARD.HOME.NO_FEEDBACK') }}
          </div>
        </div>
      </div>

      <!-- ===== RANKING · CSAT ===== -->
      <template v-if="csatLoaded && csatRanking.length">
        <div class="sec-label">
          {{ $t('DASHBOARD.HOME.RANKING') }}
          <span class="hint">· {{ $t('DASHBOARD.HOME.RANKING_HINT') }}</span>
        </div>
        <div class="grid grid-rank">
          <!-- Pódio -->
          <div class="card">
            <div class="card-h">
              <h3>{{ $t('DASHBOARD.HOME.PODIUM') }}</h3>
              <span class="pill pill-brand">{{ $t('DASHBOARD.HOME.TOP3') }}</span>
            </div>
            <div class="podium">
              <div
                v-for="(p, idx) in csatPodium"
                :key="p.id"
                class="pod"
                :class="`pod-pos-${podiumRank(idx)}`"
              >
                <span class="pod-av" :style="{ background: p.gradient }">
                  {{ p.initials }}
                  <span class="medal" :class="`medal-${podiumRank(idx)}`">{{ podiumRank(idx) }}</span>
                </span>
                <div class="pod-name">{{ p.name }}</div>
                <div class="pod-score">{{ p.score }}%</div>
                <div class="pod-sub">
                  {{ $t('DASHBOARD.HOME.RATINGS_COUNT', { count: p.total }) }}
                </div>
                <div class="pod-base">{{ podiumRank(idx) }}</div>
              </div>
            </div>
          </div>

          <!-- Classificação completa -->
          <div class="card">
            <div class="card-h">
              <h3>{{ $t('DASHBOARD.HOME.FULL_RANKING') }}</h3>
            </div>
            <div class="rank-list">
              <div v-for="(a, i) in csatRanking" :key="a.id" class="rank-row">
                <span class="rank-badge" :class="{ 'rank-top': i < 3 }">{{ i + 1 }}</span>
                <span class="rank-av" :style="{ background: a.gradient }">{{ a.initials }}</span>
                <div class="rank-name">
                  {{ a.name
                  }}<small>{{
                    $t('DASHBOARD.HOME.RATINGS_COUNT', { count: a.total })
                  }}</small>
                </div>
                <span class="rank-score">{{ a.score }}%</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MetricCardInboxTeam from './Components/MetricCardInboxTeam.vue';
import BlockLoader from './Components/BlockLoader.vue';
import alertMixin from 'shared/mixins/alertMixin';
import reportMixin from 'dashboard/mixins/reportMixin';
import timeMixin from 'dashboard/mixins/time';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { formatTime } from '@chatwoot/utils';
import { frontendURL } from '../../../helper/URLHelper';
import router from '../..';

export default {
  name: 'Home',
  components: { MetricCardInboxTeam, BlockLoader },
  mixins: [reportMixin, alertMixin, timeMixin],
  data() {
    return {
      isFullscreen: false,
      loading: false,
      pageIndex: 1,
      getterKey: 'inboxes/getInboxes',
      actionKey: 'inboxes/get',
      type: 'inbox',
      dashFilterId: null,
      dashDurationDays: 7,
      csatLoaded: false,
    };
  },
  computed: {
    ...mapGetters({
      agentStatus: 'agents/getAgentStatus',
      agents: 'agents/getAgents',
      agentConversationMetric: 'getAgentConversationMetric',
      accountConversationMetric: 'getAccountConversationMetric',
      accountSummary: 'getAccountSummary',
      accountInboxSummary: 'getAccountInboxSummary',
      inboxes: 'inboxes/getInboxes',
      teams: 'teams/getTeams',
      csatMetrics: 'csat/getMetrics',
      csatSatisfaction: 'csat/getSatisfactionScore',
      csatResponseRate: 'csat/getResponseRate',
      csatRatingPercentage: 'csat/getRatingPercentage',
      csatResponses: 'csat/getCSATResponses',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
    }),

    // ---- CSAT (Qualidade) ----
    csatScore() {
      return Math.round(Number(this.csatSatisfaction) || 0);
    },
    csatRespRate() {
      return Math.round(Number(this.csatResponseRate) || 0);
    },
    csatFcr() {
      return Math.round(Number(this.csatMetrics?.fistContactResolution) || 0);
    },
    csatPositive() {
      const p = this.csatRatingPercentage || {};
      return Math.round((Number(p[4]) || 0) + (Number(p[5]) || 0));
    },
    csatNeutral() {
      return Math.round(Number((this.csatRatingPercentage || {})[3]) || 0);
    },
    csatNegative() {
      const p = this.csatRatingPercentage || {};
      return Math.round((Number(p[1]) || 0) + (Number(p[2]) || 0));
    },
    csatPill() {
      const s = this.csatScore;
      if (s >= 85)
        return { label: this.$t('DASHBOARD.HOME.CSAT_PILL.GOOD'), cls: 'pill-good' };
      if (s >= 60)
        return {
          label: this.$t('DASHBOARD.HOME.CSAT_PILL.REGULAR'),
          cls: 'pill-warn',
        };
      return { label: this.$t('DASHBOARD.HOME.CSAT_PILL.BAD'), cls: 'pill-crit' };
    },
    // Ranking de agentes por satisfação (agregado das respostas)
    csatRanking() {
      const AV_COLORS = [
        ['#7fc7f5', '#4f9de8'],
        ['#8fd7a0', '#5cbf7a'],
        ['#a89ef0', '#7a5cf0'],
        ['#f0a9c8', '#e06fa0'],
        ['#f5a97f', '#e8804f'],
        ['#ffb56b', '#f78e3d'],
      ];
      const byAgent = {};
      (this.csatResponses || []).forEach(r => {
        const id = r.assigned_agent_id || (r.assigned_agent && r.assigned_agent.id);
        const name =
          r.assigned_agent_name || (r.assigned_agent && r.assigned_agent.name);
        if (!id) return;
        if (!byAgent[id]) {
          byAgent[id] = { id, name: name || 'Agente', total: 0, positive: 0 };
        }
        byAgent[id].total += 1;
        if ((r.rating || 0) >= 4) byAgent[id].positive += 1;
      });
      return Object.values(byAgent)
        .map(a => {
          const nm = a.name;
          let hash = 0;
          for (let i = 0; i < nm.length; i += 1) {
            hash = (hash * 31 + nm.charCodeAt(i)) % 1000000007;
          }
          const [from, to] = AV_COLORS[Math.abs(hash) % AV_COLORS.length];
          return {
            ...a,
            score: a.total ? Math.round((a.positive / a.total) * 100) : 0,
            gradient: `linear-gradient(135deg, ${from}, ${to})`,
            initials: nm
              .split(/\s+/)
              .slice(0, 2)
              .map(p => p.charAt(0).toUpperCase())
              .join(''),
          };
        })
        .sort((a, b) => b.score - a.score || b.total - a.total);
    },
    csatPodium() {
      const r = this.csatRanking;
      // ordem visual: 2º, 1º, 3º
      return [r[1], r[0], r[2]].filter(Boolean);
    },
    recentFeedback() {
      return (this.csatResponses || [])
        .filter(r => r.feedback_message)
        .slice(0, 4)
        .map(r => {
          const rating = r.rating || 0;
          const good = rating >= 4;
          const neutral = rating === 3;
          return {
            id: r.id,
            emoji: good ? '😀' : neutral ? '😐' : '☹️',
            emojiClass: good ? 'em-good' : neutral ? 'em-warn' : 'em-crit',
            message: r.feedback_message,
            contact:
              r.contact?.name || r.contact_name || r.sender?.name || 'Cliente',
            agent:
              r.assigned_agent?.name ||
              r.assigned_agent_name ||
              (r.assigned_agent && r.assigned_agent.available_name) ||
              '',
          };
        });
    },

    // Opções do 2º chip: caixas de entrada ou times, conforme o tipo
    dashFilterOptions() {
      return this.type === 'team' ? this.teams || [] : this.inboxes || [];
    },

    // Filtros do cabeçalho (canal + período) que controlam o gráfico
    durationOptions() {
      return [7, 30, 90].map(value => ({
        value,
        label: this.$t('DASHBOARD.HOME.LAST_DAYS', { count: value }),
      }));
    },
    dashTo() {
      return getUnixEndOfDay(new Date());
    },
    dashFrom() {
      const d = new Date();
      d.setDate(d.getDate() - (this.dashDurationDays - 1));
      return getUnixStartOfDay(d);
    },
    currentFilterName() {
      const opt = this.dashFilterOptions.find(i => i.id === this.dashFilterId);
      return opt ? opt.name : '';
    },

    // % de resolvidas sobre o total (real)
    resolvedPercent() {
      const total = Number(this.accountSummary?.conversations_count) || 0;
      const resolved = Number(this.accountSummary?.resolutions_count) || 0;
      if (!total) return '0%';
      return `${Math.round((resolved / total) * 100)}%`;
    },

    // taxa de contenção do bot (resolvidas / total pelo bot)
    botContentionRate() {
      const total = Number(this.accountSummary?.bot_dashboard_conversation_count) || 0;
      const resolved = Number(this.accountSummary?.bot_dashboard_resolution_count) || 0;
      if (!total) return '—';
      return `${Math.round((resolved / total) * 100)}%`;
    },

    // Carga por agente: nome, status, avatar e barra proporcional ao nº de conversas abertas
    agentLoad() {
      const AV_COLORS = [
        ['#7fc7f5', '#4f9de8'],
        ['#8fd7a0', '#5cbf7a'],
        ['#a89ef0', '#7a5cf0'],
        ['#f0a9c8', '#e06fa0'],
        ['#f5a97f', '#e8804f'],
        ['#ffb56b', '#f78e3d'],
      ];
      const STATUS_COLOR = {
        online: '#1f9d63',
        busy: '#f5a623',
        offline: '#8a97a3',
      };
      const metrics = this.agentConversationMetric || [];
      const counts = metrics.map(m => Number(m?.metric?.open) || 0);
      const max = Math.max(1, ...counts);
      return (this.agents || [])
        .map(agent => {
          const name =
            agent.name ||
            agent.available_name ||
            this.$t('DASHBOARD.HOME.AGENT');
          let hash = 0;
          for (let i = 0; i < name.length; i += 1) {
            hash = (hash * 31 + name.charCodeAt(i)) % 1000000007;
          }
          const [from, to] = AV_COLORS[Math.abs(hash) % AV_COLORS.length];
          const status = agent.availability_status || 'offline';
          const metric = metrics.find(m => m.id === agent.id);
          const open = Number(metric?.metric?.open) || 0;
          return {
            id: agent.id,
            name,
            initials: name
              .split(/\s+/)
              .slice(0, 2)
              .map(p => p.charAt(0).toUpperCase())
              .join(''),
            gradient: `linear-gradient(135deg, ${from}, ${to})`,
            status,
            statusColor: STATUS_COLOR[status] || STATUS_COLOR.offline,
            loadLabel:
              status === 'offline'
                ? this.$t('DASHBOARD.OFFLINE')
                : this.$t('DASHBOARD.HOME.IN_SERVICE', { count: open }),
            pct: Math.round((open / max) * 100),
          };
        })
        .sort((a, b) => b.pct - a.pct)
        .slice(0, 6);
    },

    conversationByAgentCounter() {
      const result =
        this.accountSummary?.conversations_count / this.agents?.length;
      if (!isNaN(result)) {
        const formatted_number = result % 1 !== 0 ? result.toFixed(2) : result;
        return formatted_number;
      }
      return 0;
    },

    conversationByAgentCounterInboxTeam() {
      const result =
        this.accountInboxSummary?.conversations_count / this.agents?.length;
      if (!isNaN(result)) {
        const formatted_number = result % 1 !== 0 ? result.toFixed(2) : result;
        return formatted_number;
      }
      return 0;
    },

    mediumWaitingTime() {
      const temp = formatTime(this.accountSummary?.avg_first_response_time);
      return this.formatDate(temp);
    },

    mediumTimeService() {
      const temp = formatTime(this.accountSummary?.avg_resolution_time);
      return this.formatDate(temp);
    },

    mediumTimeServiceInboxTeam() {
      const temp = formatTime(this.accountInboxSummary?.avg_resolution_time);
      return this.formatDate(temp);
    },

    mediumTimeResponse() {
      const temp = formatTime(this.accountSummary?.avg_handling_time || 0);
      return this.formatDate(temp);
    },

    mediumTimeResponseInboxTeam() {
      const temp = formatTime(this.accountInboxSummary?.avg_handling_time || 0);
      return this.formatDate(temp);
    },

    mediumWaitingTimeInboxTeam() {
      const temp = formatTime(
        this.accountInboxSummary?.avg_first_response_time
      );
      return this.formatDate(temp);
    },
  },

  watch: {
    // define o item padrão (primeira caixa/time) quando a lista carrega ou o tipo muda
    dashFilterOptions: {
      immediate: true,
      handler(list) {
        const exists = list.some(o => o.id === this.dashFilterId);
        if ((!this.dashFilterId || !exists) && list && list.length) {
          this.dashFilterId = list[0].id;
        }
      },
    },
  },

  mounted() {
    this.have_permission();
    this.loading = true;

    const promises = [];

    promises.push(this.fetchData());
    promises.push(
      this.$store.dispatch('fetchAccountConversationMetric', { type: 'account' })
    );
    promises.push(this.$store.dispatch('agents/get'));
    promises.push(
      this.$store.dispatch('fetchAgentConversationMetric', {
        type: 'agent',
        page: this.pageIndex,
      })
    );

    Promise.all(promises)
      .catch(() => {
        this.showAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    formatDate(temp) {
      const sec = this.$t('DASHBOARD.DATE.SECOND');
      const day = this.$t('DASHBOARD.DATE.DAY');
      const formattedTime = temp?.replace('Sec', sec).replace('Day', day);
      return formattedTime;
    },
    fetchData() {
      try {
        const payload = {
          businessHours: false,
          from: this.from(),
          to: this.to(),
          groupBy: 'day',
        };

        return this.$store.dispatch('fetchAccountSummary', payload);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    to() {
      return getUnixEndOfDay(new Date());
    },

    from() {
      return getUnixStartOfDay(new Date());
    },

    handleInboxTeam(selectedValue) {
      if (selectedValue === 'team') {
        this.type = 'team';
        this.getterKey = 'teams/getTeams';
        this.actionKey = 'teams/get';
      } else {
        this.type = 'inbox';
        this.getterKey = 'inboxes/getInboxes';
        this.actionKey = 'inboxes/get';
      }
    },

    // Troca entre Caixa de entrada / Time no cabeçalho do dashboard
    onDashTypeChange(event) {
      const value = event.target.value;
      this.handleInboxTeam(value);
      this.dashFilterId = null;
      this.$store.dispatch(value === 'team' ? 'teams/get' : 'inboxes/get');
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    },
    enterFullscreen() {
      const fullscreenDiv = this.$refs.fullscreenDiv;
      if (fullscreenDiv.requestFullscreen) {
        fullscreenDiv.requestFullscreen();
      } else if (fullscreenDiv.mozRequestFullScreen) {
        fullscreenDiv.mozRequestFullScreen();
      } else if (fullscreenDiv.webkitRequestFullscreen) {
        fullscreenDiv.webkitRequestFullscreen();
      } else if (fullscreenDiv.msRequestFullscreen) {
        fullscreenDiv.msRequestFullscreen();
      }
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },
    have_permission() {
      if (!this.currentUser.permissions.dashboard && this.isSupervisor) {
        const path = frontendURL(`accounts/${this.accountId}/conversation`);
        router.push({ path });
      }
    },
    podiumRank(visualIndex) {
      // ordem visual [2º, 1º, 3º] → posição real
      return [2, 1, 3][visualIndex] || visualIndex + 1;
    },
    loadCsatData() {
      this.csatLoaded = true;
      const to = getUnixEndOfDay(new Date());
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 89);
      const payload = { from: getUnixStartOfDay(fromDate), to };
      this.$store.dispatch('csat/getMetrics', payload);
      this.$store.dispatch('csat/get', payload);
    },
    goToConversations() {
      router.push({
        path: frontendURL(`accounts/${this.accountId}/conversation`),
      });
    },
    goToAgents() {
      router.push({
        path: frontendURL(`accounts/${this.accountId}/settings/agents/list`),
      });
    },
    refreshData() {
      this.loading = true;
      Promise.all([
        this.fetchData(),
        this.$store.dispatch('fetchAccountConversationMetric', {
          type: 'account',
        }),
        this.$store.dispatch('agents/get'),
        this.$store.dispatch('fetchAgentConversationMetric', {
          type: 'agent',
          page: this.pageIndex,
        }),
      ])
        .catch(() => this.showAlert(this.$t('REPORT.DATA_FETCHING_FAILED')))
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
// Tokens do protótipo (dashboard) mapeados
$surface: #ffffff;
$surface-2: #f0f3f5;
$border: #e4e9ee;
$border-strong: #d3dbe2;
$text: #0f1f2a;
$text-2: #516170;
$text-3: #8a97a3;
$brand: #01c6d8;
$success: #1f9d63;
$success-bg: #e7f6ee;
$warning: #f5a623;
$warning-bg: #fdf2e2;
$danger: #dc4b4b;
$danger-bg: #fdecec;
$purple: #7a5cf0;

.dash-page {
  position: relative;
  height: 100%;
  min-height: 0;
  // preenche toda a largura do conteúdo (evita a faixa branca à direita)
  flex: 1 1 0%;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-background, #f6f9fb);

  &.fullscreen {
    background: var(--color-background, #f6f9fb);
  }
}

.dash {
  flex: 1;
  overflow-y: auto;
  padding: 0 var(--space-medium) var(--space-large);
}

.dash-head {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--color-background, #f6f9fb);
  padding: var(--space-two) 0 var(--space-slab);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-normal);

  h1 {
    font-size: 2.1rem;
    font-weight: var(--font-weight-black);
    letter-spacing: -0.3px;
    display: flex;
    align-items: center;
    gap: var(--space-one);
    margin: 0;
    color: $text;
  }
  .sub {
    font-size: 1.25rem;
    color: $text-3;
    font-weight: var(--font-weight-medium);
    margin-top: 2px;
  }
}

.live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  font-weight: var(--font-weight-bold);
  color: $success;
  background: $success-bg;
  padding: 4px 10px;
  border-radius: 20px;
}
.pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $success;
  box-shadow: 0 0 0 0 rgba(31, 157, 99, 0.5);
  animation: dash-pulse 2s infinite;
}
@keyframes dash-pulse {
  0% { box-shadow: 0 0 0 0 rgba(31, 157, 99, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(31, 157, 99, 0); }
  100% { box-shadow: 0 0 0 0 rgba(31, 157, 99, 0); }
}

.dash-filters {
  display: flex;
  align-items: center;
  gap: var(--space-small);
}
// Chip de filtro do cabeçalho (Canal / Duração), igual .fsel do protótipo
.fsel {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  height: 3.8rem;
  padding: 0 var(--space-slab);
  background: $surface;
  border: 1px solid $border;
  border-radius: 9px;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out;

  &:hover { border-color: $border-strong; }

  .fsel-ic {
    width: 15px; height: 15px; flex: none; color: $text-3;
    fill: none; stroke: currentColor; stroke-width: 2;
    stroke-linecap: round; stroke-linejoin: round;
  }
  .fsel-lbl { font-size: 1.3rem; color: $text-3; font-weight: var(--font-weight-medium); }
  select {
    appearance: none;
    -webkit-appearance: none;
    border: 0;
    background: transparent;
    box-shadow: none;
    font-size: 1.3rem;
    font-weight: var(--font-weight-bold);
    color: $text;
    height: 3.6rem;
    margin: 0;
    padding: 0 1.8rem 0 0;
    cursor: pointer;
    max-width: 18rem;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a97a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right center;

    &:focus { outline: none; box-shadow: none; }
  }
}
.fbtn {
  height: 38px;
  width: 38px;
  display: grid;
  place-items: center;
  background: $surface;
  border: 1px solid $border;
  border-radius: 9px;
  color: $text-2;
  cursor: pointer;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover { background: $surface-2; color: $text; }
  svg {
    width: 16px; height: 16px; fill: none; stroke: currentColor;
    stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
  }
}

.sec-label {
  font-size: 1.2rem;
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $text-3;
  margin: var(--space-two) 0 var(--space-slab);
  display: flex;
  align-items: center;
  gap: var(--space-small);

  .hint {
    font-size: 1.1rem;
    font-weight: var(--font-weight-medium);
    text-transform: none;
    letter-spacing: 0;
    color: $text-3;
  }
}

.grid { display: grid; gap: var(--space-slab); }
.g3 { grid-template-columns: repeat(3, 1fr); }
.g6 { grid-template-columns: repeat(6, 1fr); }
.g2-1 { grid-template-columns: 2fr 1fr; }
.grid-mt { margin-top: var(--space-slab); }

.card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 12px;
  padding: var(--space-normal) var(--space-normal);
}
.card-h {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-slab);

  h3 { font-size: 1.4rem; font-weight: var(--font-weight-bold); margin: 0; color: $text; }
  .more { color: $text-3; font-size: 1.2rem; font-weight: var(--font-weight-medium); cursor: pointer; background: none; }
  .more:hover { color: $brand; }
}

.stat-row { display: flex; align-items: stretch; gap: 6px; }
.stat {
  flex: 1;
  text-align: center;
  padding: 6px 4px;
  min-width: 0;

  .n { font-size: 2.6rem; font-weight: var(--font-weight-black); letter-spacing: -0.5px; line-height: 1.1; color: $text; }
  .l {
    font-size: 1.1rem; color: $text-2; font-weight: var(--font-weight-medium);
    margin-top: 3px; display: flex; align-items: center; justify-content: center; gap: 5px;
  }
  .l .d { width: 7px; height: 7px; border-radius: 50%; flex: none; }
}
.stat-good { color: $success !important; }
.stat-warn { color: $warning !important; }
.stat-muted { color: $text-3 !important; }
.stat.attn .n { color: $warning; }
.stat.crit .n { color: $danger; }
.vsep { width: 1px; background: $border; margin: 4px 0; }

.dot-brand { background: $brand; }
.dot-good { background: $success; }
.dot-warn { background: $warning; }
.dot-crit { background: $danger; }
.dot-purple { background: $purple; }
.dot-off { background: $text-3; }

.kpi {
  background: $surface;
  border: 1px solid $border;
  border-radius: 12px;
  padding: var(--space-slab) var(--space-normal);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  min-width: 0;
}
.kpi-top { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.kpi-label { font-size: 1.15rem; color: $text-2; font-weight: var(--font-weight-medium); min-width: 0; }
.kpi-ic {
  width: 26px; height: 26px; border-radius: 7px; display: grid; place-items: center; flex: none;
  svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
}
.ic-danger { background: $danger-bg; color: $danger; }
.ic-success { background: $success-bg; color: $success; }
.ic-warning { background: $warning-bg; color: $warning; }
.kpi-val { font-size: 2.4rem; font-weight: var(--font-weight-black); letter-spacing: -0.5px; line-height: 1; color: $text; white-space: nowrap; }
.val-danger { color: $danger; }
.val-success { color: $success; }
.val-warning { color: $warning; }
.kpi-foot { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.trend-note { font-size: 1.05rem; color: $text-3; font-weight: var(--font-weight-medium); }

.pill { font-size: 1.05rem; font-weight: var(--font-weight-bold); padding: 2px 8px; border-radius: 20px; }
.pill-good { background: $success-bg; color: $success; }

.agent-row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 0; border-bottom: 1px solid $border;
  &:last-child { border: none; }
  .nm { font-size: 1.25rem; font-weight: var(--font-weight-medium); flex: 1; min-width: 0; color: $text; }
  .nm.nm-off { color: $text-3; }
  .load { font-size: 1.1rem; color: $text-3; font-weight: var(--font-weight-normal); }
}
.ava {
  width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center;
  color: #fff; font-weight: var(--font-weight-bold); font-size: 1.2rem; flex: none; position: relative;
  .st { position: absolute; bottom: -1px; right: -1px; width: 9px; height: 9px; border-radius: 50%; border: 2px solid #fff; }
}
.agent-bar { width: 60px; height: 6px; border-radius: 4px; background: $surface-2; overflow: hidden; flex: none;
  i { display: block; height: 100%; border-radius: 4px; background: $brand; }
}
.empty-hint { font-size: 1.2rem; color: $text-3; padding: var(--space-small) 0; }

.card-inbox {
  ::v-deep .metric-card,
  ::v-deep .card {
    box-shadow: none; border: 0; padding: 0; background: transparent;
  }
  .inbox-stat { display: inline-flex; flex-direction: column; margin-right: var(--space-large); }
  .inbox-n { font-size: 2rem; font-weight: var(--font-weight-black); color: $text; }
  .inbox-n.emphasis { color: $brand; }
  .inbox-l { font-size: 1.1rem; color: $text-2; }

  // ---- Selects no padrão .fsel do protótipo (compacto, borda leve, cantos 9px) ----
  ::v-deep select,
  ::v-deep .multiselect__tags,
  ::v-deep .mx-input {
    height: 3.8rem;
    min-height: 3.8rem;
    padding: 0 var(--space-slab);
    background: $surface;
    border: 1px solid $border;
    border-radius: 9px;
    font-size: 1.3rem;
    font-weight: var(--font-weight-medium);
    color: $text;
    box-shadow: none;
    display: flex;
    align-items: center;
    transition: border-color 0.15s ease-in-out;
  }
  ::v-deep select {
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    // seta custom (chevron)
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a97a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right var(--space-slab) center;
    padding-right: var(--space-two);
    width: auto;
    min-width: 16rem;
    max-width: 22rem;
  }
  ::v-deep select:hover,
  ::v-deep .multiselect:hover .multiselect__tags,
  ::v-deep .mx-input:hover {
    border-color: $border-strong;
  }
  ::v-deep .multiselect,
  ::v-deep .date-picker,
  ::v-deep .mx-datepicker {
    width: 100%;
    max-width: 22rem;
  }
  ::v-deep .multiselect__single,
  ::v-deep .multiselect__placeholder {
    background: transparent;
    font-size: 1.3rem;
    color: $text;
    padding: 0;
    margin: 0;
    line-height: 3.6rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ::v-deep .multiselect__placeholder { color: $text-3; }
  ::v-deep .multiselect__select {
    height: 3.8rem;
    &::before { border-color: $text-3 transparent transparent; }
  }
  // rótulos ("Selecionar caixa de entrada", "Duração")
  ::v-deep .multiselect-wrap--small > p,
  ::v-deep .date-picker > p,
  ::v-deep .reports-filters--container p {
    font-size: 1.1rem;
    color: $text-3;
    font-weight: var(--font-weight-medium);
    margin: 0 0 var(--space-micro);
    text-transform: none;
  }
}

// ---- Qualidade / CSAT (padrão do protótipo) ----
.sec-refresh {
  margin-left: auto;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: grid;
  place-items: center;
  color: $text-3;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover { background: $surface-2; color: $text-2; }
  svg {
    width: 14px; height: 14px; fill: none; stroke: currentColor;
    stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
  }
}

.pill-warn { background: $warning-bg; color: $warning; }
.pill-crit { background: $danger-bg; color: $danger; }

.donut-wrap {
  display: flex;
  align-items: center;
  gap: var(--space-normal);
  margin: var(--space-small) 0 var(--space-normal);
}
.donut {
  flex: none;
}
.donut-track { stroke: $surface-2; }
.donut-val { stroke: $success; transition: stroke-dasharray 0.4s ease; }
.csat-num {
  font-size: 2.6rem;
  font-weight: var(--font-weight-black);
  letter-spacing: -0.5px;
  line-height: 1.1;
  color: $success;
}
.csat-sub { font-size: 1.15rem; color: $text-3; }

.satbar {
  display: flex;
  height: 8px;
  border-radius: 5px;
  overflow: hidden;
  background: $surface-2;
  margin-bottom: var(--space-small);

  span { display: block; height: 100%; }
  .bar-good { background: $success; }
  .bar-warn { background: $warning; }
  .bar-crit { background: $danger; }
}
.sat-emojis {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
  color: $text-2;
}

.ind-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
}
.ind-row {
  display: flex;
  justify-content: space-between;
  align-items: center;

  span { font-size: 1.25rem; color: $text-2; }
  b { font-size: 1.8rem; font-weight: var(--font-weight-bold); color: $text; }
}

.fb-list { display: flex; flex-direction: column; gap: var(--space-slab); }
.fb {
  display: flex;
  gap: var(--space-small);
  align-items: flex-start;

  .em {
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    font-size: 1.5rem;
  }
  .em-good { background: $success-bg; }
  .em-warn { background: $warning-bg; }
  .em-crit { background: $danger-bg; }
  .txt { font-size: 1.25rem; color: $text; line-height: 1.4; }
  .who { font-size: 1.1rem; color: $text-3; margin-top: 2px; }
}

// Prompt de carregamento do CSAT (sob demanda) — empty-state centralizado
.csat-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-small);
  text-align: center;
  padding: var(--space-large) var(--space-normal);

  .csat-prompt__ic {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #e1f8fb;
    color: #0a7581;
    display: grid;
    place-items: center;
    flex: none;
    svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  }
  .csat-prompt__txt {
    font-size: 1.3rem;
    color: $text-2;
    max-width: 42rem;
    margin-bottom: var(--space-small);
  }
  ::v-deep .button {
    height: 3.8rem;
    padding: 0 var(--space-normal);
    font-weight: var(--font-weight-bold);
  }
}

// ---- Ranking · CSAT ----
.grid-rank { grid-template-columns: 1.25fr 1fr; }
.pill-brand { background: #e1f8fb; color: #0a7581; }

.podium {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: end;
  gap: var(--space-slab);
  padding-top: var(--space-small);
}
.pod {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pod-av {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: var(--font-weight-bold);
  font-size: 1.4rem;
  margin-bottom: var(--space-small);

  .medal {
    position: absolute;
    bottom: -3px;
    right: -3px;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: #fff;
    font-size: 1rem;
    font-weight: var(--font-weight-black);
    border: 2px solid $surface;
  }
  .medal-1 { background: #e6b325; }
  .medal-2 { background: #9aa7b2; }
  .medal-3 { background: #c08457; }
}
.pod-name {
  font-size: 1.2rem;
  font-weight: var(--font-weight-medium);
  color: $text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.pod-score { font-size: 1.8rem; font-weight: var(--font-weight-black); color: $text; }
.pod-sub { font-size: 1.05rem; color: $text-3; }
.pod-base {
  margin-top: var(--space-small);
  width: 100%;
  border-radius: 8px 8px 0 0;
  background: $surface-2;
  color: $text-3;
  font-weight: var(--font-weight-black);
  font-size: 1.3rem;
  display: grid;
  place-items: center;
}
.pod-pos-1 .pod-base { height: 5rem; background: #fdf2e2; color: #b5850f; }
.pod-pos-2 .pod-base { height: 3.6rem; }
.pod-pos-3 .pod-base { height: 2.6rem; }

.rank-list { display: flex; flex-direction: column; }
.rank-row {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small) 0;
  border-bottom: 1px solid $border;
  &:last-child { border: none; }
}
.rank-badge {
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: grid;
  place-items: center;
  font-size: 1.1rem;
  font-weight: var(--font-weight-bold);
  background: $surface-2;
  color: $text-2;

  &.rank-top { background: #fdf2e2; color: #b5850f; }
}
.rank-av {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: var(--font-weight-bold);
  font-size: 1.1rem;
}
.rank-name {
  flex: 1;
  min-width: 0;
  font-size: 1.25rem;
  font-weight: var(--font-weight-medium);
  color: $text;

  small { display: block; font-size: 1.05rem; color: $text-3; font-weight: var(--font-weight-normal); }
}
.rank-score { font-size: 1.4rem; font-weight: var(--font-weight-bold); color: $success; }
</style>
